import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import DatePicker from "@/components/DatePicker";
import { ChevronLeft, ChevronRight } from "lucide-react";

// R1-03 (WORLD-024): shared Month | Year | Custom period selector, reused as-is across Business,
// Products, NEEC MONEY and Purchasing reports so date selection behaves identically everywhere.
// Produces a plain {start, end} YYYY-MM-DD pair via onChange — no calculation, no API call. Each
// caller keeps its own current-state snapshot figures (inventory value, open PO value, wallet
// balances, etc.) completely separate from and unaffected by this range, exactly as before.
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
const daysInMonth = (y, m) => new Date(y, m, 0).getDate();
const localToday = () => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() }; };
const todayYMD = () => { const t = localToday(); return ymd(t.y, t.m, t.d); };
const addDays = (y, m, d, delta) => { const dt = new Date(y, m - 1, d + delta); return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() }; };
const mondayOf = (y, m, d) => { const dow = new Date(y, m - 1, d).getDay(); return addDays(y, m, d, dow === 0 ? -6 : -(dow - 1)); };
// A generous, fixed historical window for the Month/Year quick-selects (not tied to any data lookup —
// no new calculation). Any earlier date is still reachable via Custom's manual From/To.
const YEAR_SELECT_SPAN = 10;
const inputCls = "h-8 rounded-md border border-input bg-background px-1.5 text-sm";

export default function DateRangeQuickPicker({ value, onChange, testidPrefix = "qdr", startTestId, endTestId }) {
  const now = localToday();
  const MIN_YEAR = now.y - YEAR_SELECT_SPAN;
  const MAX_YEAR = now.y;
  const [mode, setMode] = useState("custom"); // starts on Custom so the caller's existing default range is preserved until a primary mode is picked.
  const [monthAnchor, setMonthAnchor] = useState({ y: now.y, m: now.m });
  const [yearAnchor, setYearAnchor] = useState(now.y);
  const yearOptions = Array.from({ length: YEAR_SELECT_SPAN + 1 }, (_, i) => MIN_YEAR + i);

  // Keeps the anchor inside the selectable window (MIN_YEAR..MAX_YEAR, and never a future month
  // within the current year) so the controlled <select>s always show a value that's really an
  // option — a chevron or a direct select change can never desync them from the fetched range.
  const clampMonth = ({ y, m }) => {
    if (y > MAX_YEAR || (y === MAX_YEAR && m > now.m)) return { y: MAX_YEAR, m: now.m };
    if (y < MIN_YEAR) return { y: MIN_YEAR, m: 1 };
    return { y, m };
  };
  const clampYear = (y) => Math.min(MAX_YEAR, Math.max(MIN_YEAR, y));
  const atMonthMax = monthAnchor.y === MAX_YEAR && monthAnchor.m === now.m;
  const atMonthMin = monthAnchor.y === MIN_YEAR && monthAnchor.m === 1;

  const applyMonth = (a) => onChange(ymd(a.y, a.m, 1), ymd(a.y, a.m, daysInMonth(a.y, a.m)));
  const applyYear = (y) => onChange(`${y}-01-01`, y === now.y ? todayYMD() : `${y}-12-31`);

  const pick = (preset) => {
    setMode(preset);
    if (preset === "month") { const a = { y: now.y, m: now.m }; setMonthAnchor(a); applyMonth(a); }
    else if (preset === "year") { setYearAnchor(now.y); applyYear(now.y); }
    // "custom": leave the current range as-is; edited manually or via a shortcut below.
  };
  const shiftMonth = (delta) => {
    let { y, m } = monthAnchor;
    m += delta;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    const a = clampMonth({ y, m });
    setMonthAnchor(a);
    applyMonth(a);
  };
  const setMonthPart = (patch) => { const a = clampMonth({ ...monthAnchor, ...patch }); setMonthAnchor(a); applyMonth(a); };
  const setYear = (y) => { const c = clampYear(y); setYearAnchor(c); applyYear(c); };

  const shortcut = (k) => {
    const t = now;
    if (k === "week") { const mo = mondayOf(t.y, t.m, t.d); onChange(ymd(mo.y, mo.m, mo.d), todayYMD()); }
    else if (k === "lastweek") { const moThis = mondayOf(t.y, t.m, t.d); const moLast = addDays(moThis.y, moThis.m, moThis.d, -7); const suLast = addDays(moLast.y, moLast.m, moLast.d, 6); onChange(ymd(moLast.y, moLast.m, moLast.d), ymd(suLast.y, suLast.m, suLast.d)); }
    else if (k === "last7") { const from = addDays(t.y, t.m, t.d, -6); onChange(ymd(from.y, from.m, from.d), todayYMD()); }
  };

  return (
    <div className="flex flex-wrap items-end gap-2" data-testid={`${testidPrefix}-wrap`}>
      <div className="flex gap-1">
        <Button type="button" size="sm" variant={mode === "month" ? "default" : "outline"} onClick={() => pick("month")} data-testid={`${testidPrefix}-preset-month`}>Month</Button>
        <Button type="button" size="sm" variant={mode === "year" ? "default" : "outline"} onClick={() => pick("year")} data-testid={`${testidPrefix}-preset-year`}>Year</Button>
        <Button type="button" size="sm" variant={mode === "custom" ? "default" : "outline"} onClick={() => pick("custom")} data-testid={`${testidPrefix}-preset-custom`}>Custom</Button>
      </div>

      {mode === "month" && (
        <div className="flex items-center gap-1" data-testid={`${testidPrefix}-month-nav`}>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => shiftMonth(-1)} disabled={atMonthMin} data-testid={`${testidPrefix}-month-prev`} aria-label="Previous month"><ChevronLeft className="w-4 h-4" /></Button>
          <select className={inputCls} value={monthAnchor.m} onChange={(e) => setMonthPart({ m: Number(e.target.value) })} data-testid={`${testidPrefix}-month-select`}>
            {MONTH_NAMES.map((mn, i) => <option key={mn} value={i + 1} disabled={monthAnchor.y === MAX_YEAR && i + 1 > now.m}>{mn}</option>)}
          </select>
          <select className={inputCls} value={monthAnchor.y} onChange={(e) => setMonthPart({ y: Number(e.target.value) })} data-testid={`${testidPrefix}-month-year-select`}>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => shiftMonth(1)} disabled={atMonthMax} data-testid={`${testidPrefix}-month-next`} aria-label="Next month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}

      {mode === "year" && (
        <div className="flex items-center gap-1" data-testid={`${testidPrefix}-year-nav`}>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => setYear(yearAnchor - 1)} disabled={yearAnchor <= MIN_YEAR} data-testid={`${testidPrefix}-year-prev`} aria-label="Previous year"><ChevronLeft className="w-4 h-4" /></Button>
          <select className={inputCls} value={yearAnchor} onChange={(e) => setYear(Number(e.target.value))} data-testid={`${testidPrefix}-year-select`}>
            {yearOptions.map((y) => <option key={y} value={y}>{y === now.y ? "Year to Date" : y}</option>)}
          </select>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => setYear(yearAnchor + 1)} disabled={yearAnchor >= MAX_YEAR} data-testid={`${testidPrefix}-year-next`} aria-label="Next year"><ChevronRight className="w-4 h-4" /></Button>
          <span className="text-xs text-muted-foreground ml-1" data-testid={`${testidPrefix}-year-label`}>{yearAnchor === now.y ? "Jan 1 – Today" : `Jan 1 – Dec 31, ${yearAnchor}`}</span>
        </div>
      )}

      {mode === "custom" && (
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-xs">From</Label><DatePicker value={value.start} onChange={(v) => onChange(v, value.end)} data-testid={startTestId || `${testidPrefix}-custom-start`} /></div>
          <div><Label className="text-xs">To</Label><DatePicker value={value.end} onChange={(v) => onChange(value.start, v)} data-testid={endTestId || `${testidPrefix}-custom-end`} /></div>
          <div className="flex gap-1">
            <Button type="button" size="sm" variant="ghost" className="text-xs h-8" onClick={() => shortcut("week")} data-testid={`${testidPrefix}-shortcut-week`}>This Week</Button>
            <Button type="button" size="sm" variant="ghost" className="text-xs h-8" onClick={() => shortcut("lastweek")} data-testid={`${testidPrefix}-shortcut-lastweek`}>Last Week</Button>
            <Button type="button" size="sm" variant="ghost" className="text-xs h-8" onClick={() => shortcut("last7")} data-testid={`${testidPrefix}-shortcut-last7`}>Last 7 Days</Button>
          </div>
        </div>
      )}
    </div>
  );
}
